"use client";

import { useEffect } from "react";

/**
 * Runs `callback` on the first real user interaction (pointer, key, scroll,
 * touch), falling back to a browser-idle timeout for visitors who never
 * interact. Use for third-party scripts (chat widgets, analytics) that don't
 * need to load before the page is actually being used — keeps their JS
 * parse/exec off the main thread during the critical rendering window.
 */
export function useDeferUntilInteraction(callback: () => void, idleTimeoutMs = 10_000) {
  useEffect(() => {
    let fired = false;

    function run() {
      if (fired) return;
      fired = true;
      cleanup();
      callback();
    }

    const events: Array<[string, AddEventListenerOptions?]> = [
      ["pointerdown", { passive: true }],
      ["keydown"],
      ["scroll", { passive: true }],
      ["touchstart", { passive: true }],
    ];
    events.forEach(([e, opts]) => window.addEventListener(e, run, opts));

    const ric = (
      window as Window & {
        requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      }
    ).requestIdleCallback;
    const idleHandle = ric
      ? ric(run, { timeout: idleTimeoutMs })
      : (window.setTimeout(run, idleTimeoutMs) as unknown as number);

    function cleanup() {
      events.forEach(([e, opts]) => window.removeEventListener(e, run, opts));
      const cic = (window as Window & { cancelIdleCallback?: (id: number) => void })
        .cancelIdleCallback;
      if (cic) cic(idleHandle);
      else window.clearTimeout(idleHandle);
    }

    return cleanup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
